import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DailyDispatches() {
  const [dispatches, setDispatches] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    dispatch_date: new Date().toISOString().split('T')[0],
    partcode: '',
    description: '',
    customer: '',
    quantity: '',
    challan_number: '',
    vehicle_number: '',
    remarks: ''
  });

  useEffect(() => {
    fetchDispatches();
    fetchCustomers();
  }, []);

  const fetchDispatches = async () => {
    try {
      const response = await axios.get(`${API}/dispatches`);
      setDispatches(response.data);
    } catch (error) {
      console.error('Error fetching dispatches:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/dispatches`, {
        ...formData,
        quantity: parseInt(formData.quantity)
      });
      setShowAddModal(false);
      resetForm();
      fetchDispatches();
    } catch (error) {
      console.error('Error creating dispatch:', error);
      alert('Error creating dispatch');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this dispatch?')) {
      try {
        await axios.delete(`${API}/dispatches/${id}`);
        fetchDispatches();
      } catch (error) {
        console.error('Error deleting dispatch:', error);
        alert('Error deleting dispatch');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      dispatch_date: new Date().toISOString().split('T')[0],
      partcode: '',
      description: '',
      customer: '',
      quantity: '',
      challan_number: '',
      vehicle_number: '',
      remarks: ''
    });
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="daily-dispatches">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Daily Dispatches</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg transition"
          data-testid="add-dispatch-button"
        >
          + Record Dispatch
        </button>
      </div>

      {/* Dispatches Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partcode</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Challan No.</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehicle No.</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {dispatches.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                    No dispatches recorded yet. Click "Record Dispatch" to add one.
                  </td>
                </tr>
              ) : (
                dispatches.map((dispatch) => (
                  <tr key={dispatch.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-700">{dispatch.dispatch_date}</td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{dispatch.partcode}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{dispatch.description}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{dispatch.customer}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-green-600">{dispatch.quantity}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{dispatch.challan_number || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{dispatch.vehicle_number || '-'}</td>
                    <td className="px-6 py-4 text-sm">
                      <button
                        onClick={() => handleDelete(dispatch.id)}
                        className="text-red-600 hover:text-red-800"
                        data-testid={`delete-dispatch-${dispatch.id}`}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Dispatch Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Record Daily Dispatch</h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dispatch Date *</label>
                  <input
                    type="date"
                    value={formData.dispatch_date}
                    onChange={(e) => setFormData({ ...formData, dispatch_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                    data-testid="dispatch-date-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer *</label>
                  <select
                    value={formData.customer}
                    onChange={(e) => setFormData({ ...formData, customer: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                    data-testid="dispatch-customer-select"
                  >
                    <option value="">Select Customer</option>
                    {customers.map(customer => (
                      <option key={customer.id} value={customer.name}>{customer.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Partcode *</label>
                  <input
                    type="text"
                    value={formData.partcode}
                    onChange={(e) => setFormData({ ...formData, partcode: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                    data-testid="dispatch-partcode-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantity *</label>
                  <input
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                    min="1"
                    data-testid="dispatch-quantity-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                  data-testid="dispatch-description-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Challan Number</label>
                  <input
                    type="text"
                    value={formData.challan_number}
                    onChange={(e) => setFormData({ ...formData, challan_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    data-testid="dispatch-challan-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Number</label>
                  <input
                    type="text"
                    value={formData.vehicle_number}
                    onChange={(e) => setFormData({ ...formData, vehicle_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    data-testid="dispatch-vehicle-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows="3"
                  data-testid="dispatch-remarks-input"
                ></textarea>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                  data-testid="cancel-dispatch-button"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                  data-testid="submit-dispatch-button"
                >
                  Record Dispatch
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
